import React, { FC, useCallback, useState } from 'react';
import { BusIcon } from '@fronton/icons-react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalSize,
  Portal,
  RegularButton,
  Typography,
} from 'fronton-react';
import { useSelector } from 'react-redux';
import { analyticsUtils } from 'utils';
import { getStoreDetail } from 'features/store/ducks/get-store-by-id';
import { getTitle } from 'features/page-model/ducks/get-model';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { StoreAddress } from 'features/store-common-info/store-adress';
import { NearestSubway } from 'features/store-common-info/store-subway';
import { adressWrap, howToGetToWrap, modalStyle, subwayWrap, transportHeading } from './styles';
import { RouteBlock } from '../route-block';

export const HowToGetTo: FC = () => {
  const { t } = useTranslation();
  const {
    howToGetTo,
    address,
    nearestSubwayStation = '',
    geocoordinateLongitude: long,
    geocoordinateLatitude: lat,
  } = useSelector(getStoreDetail) || {};
  const title = useSelector(getTitle);
  const [howToGetModalShow, setHowToGetModalShow] = useState(false);
  const { smMobile, mobile } = useBreakpoints() ?? {};
  const modalSize = smMobile || mobile ? 'full-screen' : 's';
  const route = t('route');
  const transport = t('transport');
  const shopAddress = t('shopAddress');
  const hotToGetByTransport = t('hotToGetByTransport');
  const close = t('ui.close');

  const navigateToMap = useCallback(() => {
    setHowToGetModalShow(false);
    window.open(`https://maps.yandex.ru/?rtext=~${lat},${long}&rtt=mt`, '_blank');
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: `${title} / ${shopAddress}`, // название блока
      elementText: route, // текст кнопки (Как добраться транспортом, Товары в магазине, План магазина, Фото магазина)
    });
  }, [lat, long, title, shopAddress, route]);

  const handleOpen = useCallback(() => {
    setHowToGetModalShow(true);

    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: title, // название блока
      elementText: hotToGetByTransport, // текст кнопки (Как добраться транспортом, Товары в магазине, План магазина, Фото магазина)
    });
  }, [title, hotToGetByTransport]);

  const handleClose = useCallback(() => {
    setHowToGetModalShow(false);

    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: `${title} / ${shopAddress}`, // название блока
      elementText: close, // текст элемента
    });
  }, [title, shopAddress, close]);

  return (
    <>
      <RouteBlock
        label={hotToGetByTransport}
        icon={<BusIcon size="xl" type="outline" />}
        iconActive={<BusIcon size="xl" type="duotone" />}
        onClick={handleOpen}
        qa="route-button"
      />

      <Portal>
        <Modal
          className={modalStyle}
          size={modalSize as ModalSize}
          show={howToGetModalShow}
          onClose={handleClose}
        >
          <ModalHeader label={title} title={shopAddress} />
          <ModalContent>
            {address && <StoreAddress className={adressWrap} blockName={`${title} / ${shopAddress}`} />}
            {!!nearestSubwayStation.length && <NearestSubway className={subwayWrap} />}
            <Typography variant="m" size="body_accent" className={transportHeading}>
              {transport}
            </Typography>
            <Typography variant="m" size="body_long" className={howToGetToWrap}>
              {howToGetTo}
            </Typography>
          </ModalContent>
          <ModalFooter>
            <RegularButton variant="secondary" size="l" wide onClick={navigateToMap}>
              {route}
            </RegularButton>
          </ModalFooter>
        </Modal>
      </Portal>
    </>
  );
};
